import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { Api } from "../../../common/service/Api";
import { Button, Modal, SimpleCheckboxControlled, useSnackBar } from "@truevault/aquilo";
import { Routes } from "../../root/routes";
import { Disableable } from "../../components/Disableable";
import { isVendorDocumentationClassified } from "../data-map/vendor-documentation/VendorDocumentationPage";
import { persistVendorDocumentationContactSuccess } from "../data-map/vendor-documentation/vendorDocumentationContactSuccessStorage";
import {
  getDefaultEmailBodyForVendor,
  getMissingVendorDocumentationProperties,
  MISSING_DOC_LABELS,
} from "../data-map/vendor-documentation/vendorDocumentationEmailUtils";
import { useLocalStorage } from "../../hooks/useLocalStorage";

function formatUnselectedVendorsBody(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return `${names[0]} will not be contacted.`;
  if (names.length === 2) return `${names[0]} and ${names[1]} will not be contacted.`;
  const last = names[names.length - 1];
  const rest = names.slice(0, -1).join(", ");
  return `${rest}, and ${last} will not be contacted.`;
}

const REMIND_LATER_DAYS = 7;
const OVERDUE_MONTHS = 2;

function formatVendorDocSnoozeReminderDate(d: Date): string {
  const month = d.toLocaleDateString(undefined, { month: "long" });
  const year = d.getFullYear();
  const day = d.getDate();
  const ord =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  return `${month} ${day}${ord}, ${year}`;
}

function getTwoMonthsAgo(): Date {
  const d = new Date();
  d.setMonth(d.getMonth() - OVERDUE_MONTHS);
  return d;
}

function isVendorContactOverdue(vendor: OrganizationDataRecipientDto): boolean {
  const sent = vendor.serviceProviderInfoRequestSentDate;
  if (!sent) return true; // never contacted
  return new Date(sent) < getTwoMonthsAgo();
}

/** True if any vendor had a service-provider documentation request sent within the last 2 months. */
function hasAnyRecentServiceProviderContact(vendorList: OrganizationDataRecipientDto[]): boolean {
  const cutoff = getTwoMonthsAgo();
  return vendorList.some(
    (v) =>
      v.serviceProviderInfoRequestSentDate &&
      new Date(v.serviceProviderInfoRequestSentDate) >= cutoff,
  );
}

export function VendorDocumentationOverdueAlert({
  org,
  vendors,
  refreshVendors,
  hideUrgentHeading = false,
}: {
  org: OrganizationDto;
  vendors: OrganizationDataRecipientDto[];
  refreshVendors: () => void;
  hideUrgentHeading?: boolean;
}) {
  const [notAllSelectedConfirmModalOpen, setNotAllSelectedConfirmModalOpen] = useState(false);
  const [remindConfirmModalOpen, setRemindConfirmModalOpen] = useState(false);
  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<string>>(() => new Set());
  const [sending, setSending] = useState(false);
  const snackbar = useSnackBar();

  const [remindLaterUntil, setRemindLaterUntil] = useLocalStorage(
    `vendor-doc-overdue-remind-until-${org?.id || ""}`,
    "",
  );

  const { contactableVendors, shouldShow } = useMemo(() => {
    if (!org) return { contactableVendors: [], shouldShow: false };
    const allUnclassified = (vendors || []).filter((v) => !isVendorDocumentationClassified(org, v));
    // Only show vendors who haven't been contacted in 2+ months (or never contacted)
    const overdueUnclassified = allUnclassified.filter(isVendorContactOverdue);
    const contactable: OrganizationDataRecipientDto[] = [];
    for (const v of overdueUnclassified) {
      const email = v.email || v.defaultEmail;
      if (email && email.trim()) {
        contactable.push(v);
      }
    }
    const remindLaterPassed = !remindLaterUntil || new Date(remindLaterUntil) < new Date();
    const recentContact = hasAnyRecentServiceProviderContact(vendors || []);
    // Must have at least one vendor we can email; overdue-without-email alone used to leave an empty list.
    const show = contactable.length > 0 && remindLaterPassed && !recentContact;
    return {
      contactableVendors: contactable,
      shouldShow: show,
    };
  }, [vendors, org, remindLaterUntil]);

  useEffect(() => {
    setSelectedVendorIds(new Set(contactableVendors.map((v) => v.vendorId)));
  }, [contactableVendors]);

  const allSelected =
    contactableVendors.length > 0 && selectedVendorIds.size === contactableVendors.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedVendorIds(new Set(contactableVendors.map((v) => v.vendorId)));
    } else {
      setSelectedVendorIds(new Set());
    }
  };

  const handleSelectVendor = (vendorId: string, checked: boolean) => {
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(vendorId);
      else next.delete(vendorId);
      return next;
    });
  };

  const handleRemindLaterConfirm = () => {
    const until = new Date();
    until.setDate(until.getDate() + REMIND_LATER_DAYS);
    setRemindLaterUntil(until.toISOString());
    setRemindConfirmModalOpen(false);
    snackbar(
      `We will remind you to contact vendors regarding documentation on ${formatVendorDocSnoozeReminderDate(
        until,
      )}.`,
      { variant: "info" },
    );
  };

  const selectedVendors = useMemo(
    () => contactableVendors.filter((v) => selectedVendorIds.has(v.vendorId)),
    [contactableVendors, selectedVendorIds],
  );
  const isMailboxConnected = org?.mailbox?.canSendEmail === true;
  const unselectedVendorNames = useMemo(
    () => contactableVendors.filter((v) => !selectedVendorIds.has(v.vendorId)).map((v) => v.name),
    [contactableVendors, selectedVendorIds],
  );

  const runContactVendors = async () => {
    if (selectedVendors.length === 0) return;
    setSending(true);
    try {
      const requests = selectedVendors.map((v) => ({
        vendorId: v.vendorId,
        body: getDefaultEmailBodyForVendor(org, v),
      }));
      const res = await Api.organizationVendor.requestVendorDocumentationBulk(org.id, requests);
      if (res?.ok === true) {
        const contactDate = new Date().toISOString();
        const contactedNames = selectedVendors.map((v) => v.name);
        const contactedVendorIds = selectedVendors.map((v) => v.vendorId);
        persistVendorDocumentationContactSuccess(org.id, {
          date: contactDate,
          names: contactedNames,
          vendorIds: contactedVendorIds,
        });
        const dateStr = new Date(contactDate).toLocaleDateString(undefined, {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
        snackbar("The vendors listed below were contacted successfully on " + dateStr + ".", {
          variant: "success",
          body: (
            <ul className="mt-2 list-disc pl-5">
              {contactedNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          ),
        });
        refreshVendors();
        setNotAllSelectedConfirmModalOpen(false);
      } else {
        snackbar("There was an error sending the emails.", { variant: "error" });
      }
    } catch {
      snackbar("There was an error sending the emails.", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  const handleContactClick = () => {
    if (allSelected) {
      runContactVendors();
    } else {
      setNotAllSelectedConfirmModalOpen(true);
    }
  };

  const handleContactSelectedVendorsConfirm = () => {
    runContactVendors();
  };

  if (!shouldShow) return null;

  return (
    <>
      {!hideUrgentHeading && (
        <div className="tw-boundary">
          <div className="text-[#4D260E] text-xl font-medium mb-3">
            Urgent items requiring your attention!
          </div>
        </div>
      )}
      <div className="dashboard--survey-question-pane-without-animation tw-boundary">
        <div className="flex items-center gap-3 mb-3">
          <ExclamationTriangleIcon className="text-amber-700 w-[16px] h-[14px]" />
          <span className="text-amber-800 text-base font-medium">
            Vendor Documentation Requirements
          </span>
        </div>
        <p className="text-neutral-900 font-bold text-lg mb-2">
          Compliance documents are needed for some vendors.
        </p>
        <p className="text-neutral-800 text-sm mb-4">
          Check your privacy inbox to see whether some of the vendors below have answered and
          provided needed documents. If yes, add these documents to{" "}
          <Link to={Routes.dataMap.vendorDocumentation} className="text-primary-600 underline">
            Vendor Docs
          </Link>
          . If no response - contact these vendors again.
        </p>

        <div className="max-h-[240px] overflow-y-auto">
          {contactableVendors.length > 0 && (
            <div className="bg-neutral-50 px-4 pb-3">
              <SimpleCheckboxControlled
                name="select-all"
                checked={allSelected}
                onChange={handleSelectAll}
                label="Select All"
              />
            </div>
          )}
          <ul>
            {contactableVendors.map((vendor) => {
              const missingProps = getMissingVendorDocumentationProperties(org, vendor);
              const missingLabels = missingProps.map((p) => MISSING_DOC_LABELS[p]);
              return (
                <li key={vendor.vendorId} className="px-4 pb-3 text-xs">
                  <div className="flex items-start gap-3">
                    <div className="pt-0.5">
                      <SimpleCheckboxControlled
                        name={`vendor-${vendor.vendorId}`}
                        checked={selectedVendorIds.has(vendor.vendorId)}
                        onChange={(checked) => handleSelectVendor(vendor.vendorId, checked)}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-neutral-900 flex items-center gap-2 flex-wrap">
                        {vendor.logoUrl ? (
                          <img src={vendor.logoUrl} alt={vendor.name} className="h-4 w-4" />
                        ) : null}
                        {vendor.name}
                        {vendor.serviceProviderInfoRequestSentDate && (
                          <span className="font-normal text-primary-600">
                            Vendor was contacted at{" "}
                            {new Date(vendor.serviceProviderInfoRequestSentDate).toLocaleDateString(
                              undefined,
                              {
                                month: "long",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </span>
                        )}
                      </div>
                      {missingLabels.length > 0 && (
                        <div className="mt-1 text-xs text-neutral-600">
                          {missingLabels.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex items-center gap-4 mt-4">
          <Disableable
            disabled={!isMailboxConnected}
            disabledTooltip="The email cannot be sent because your privacy email has been disconnected. Please visit your privacy inbox settings to reconnect it."
          >
            <Button
              color="primary"
              onClick={handleContactClick}
              disabled={selectedVendorIds.size === 0 || sending || !isMailboxConnected}
            >
              {sending ? "Sending…" : "Contact Vendors"}
            </Button>
          </Disableable>
          <button
            type="button"
            onClick={() => setRemindConfirmModalOpen(true)}
            className="text-primary-600 font-medium text-sm hover:underline"
          >
            Remind Me Later
          </button>
        </div>
      </div>

      {notAllSelectedConfirmModalOpen && (
        <Modal
          title="You haven't selected to contact all listed vendors"
          body={formatUnselectedVendorsBody(unselectedVendorNames)}
          firstButton="Go Back"
          secondButton={sending ? "Sending…" : "Contact Selected Vendors"}
          firstButtonColor="tertiary"
          secondButtonColor="primary"
          alignButton="right"
          buttonPadding="px-3 pb-3"
          alignText="left"
          closeButton={true}
          onFirstButtonClick={() => setNotAllSelectedConfirmModalOpen(false)}
          onSecondButtonClick={sending ? () => {} : handleContactSelectedVendorsConfirm}
          onClose={() => setNotAllSelectedConfirmModalOpen(false)}
          showOverlay={true}
          closeOnOverlayClick={true}
        />
      )}

      {remindConfirmModalOpen && (
        <Modal
          title="We will remind you to contact vendors for their documentation in a week."
          firstButton="Cancel"
          secondButton="Snooze reminder"
          firstButtonColor="tertiary"
          secondButtonColor="primary"
          alignButton="right"
          buttonPadding="px-3 pb-3"
          alignText="left"
          closeButton={true}
          onFirstButtonClick={() => setRemindConfirmModalOpen(false)}
          onSecondButtonClick={handleRemindLaterConfirm}
          onClose={() => setRemindConfirmModalOpen(false)}
          showOverlay={true}
          closeOnOverlayClick={true}
        />
      )}
    </>
  );
}
